import type { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { StarIcon } from '../../../assets/icons/StarIcon';
import type { SelectOption } from './common/Select';
import Select from './common/Select';
import ProductImage from './common/ProductImage/ProductImage';
import { useMemo } from 'react';
import { PlusIcon } from '../../../assets/icons/PlusIcon';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: #2e2e2e;
`;

const StarRow = styled.div`
  display: flex;
  gap: 4px;
`;

const Star = styled.button<{ selected: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled.input`
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  outline: none;
`;

const TextArea = styled.textarea`
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  min-height: 120px;
  resize: vertical;
  outline: none;
`;

const Optional = styled.span`
  color: var(--Text-on-Dark-BG-30, #b0b0b0);
  font-feature-settings: 'ss10' on;
  /* Caption 1/Regular */
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 16.8px */
`;

const UploadArea = styled.label`
  border: 1px dashed #b0b0b0;
  border-radius: 8px;
  width: 100%;
  height: 100px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border 0.2s;
  margin-top: 4px;
  &:hover {
    border-color: #3d1690;
  }
`;

const ImageUploadBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
  flex-wrap: wrap;
`;

const Thumbnail = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: visible;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RemoveButton = styled.button`
  position: absolute !important;
  top: -10px !important;
  right: -10px !important;
  background: #fff !important;
  border: 1px solid #ccc !important;
  border-radius: 50% !important;
  width: 20px !important;
  height: 20px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  font-size: 16px !important;
  z-index: 2 !important;
`;

const SmallUploadArea = styled(UploadArea)`
  width: 100px;
  height: 100px;
  min-width: 100px;
  min-height: 100px;
  margin-top: 0;
`;

const FileInput = styled.input`
  display: none;
`;

const Row = styled.div`
  display: flex;
  gap: 4px;
`;

const BottomButtonRow = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;
  background: transparent;
  display: flex;
  justify-content: center;
  z-index: 10;
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  background: ${(props) => (props.disabled ? '#ede8fa' : '#3d1690')} !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  height: 48px !important;
  width: 100% !important;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')} !important;
  opacity: 1 !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: background 0.2s !important;
`;

const SubText = styled.p`
  font-size: 14px;
  color: #737373;
  margin: 0 0 8px 0;
`;

const GuideText = styled.p`
  font-size: 12px;
  color: #737373;
  margin: 0 0 0 0;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const RadioLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #2e2e2e;
  cursor: pointer;
`;

const Radio = styled.input`
  accent-color: #3d1690;
  width: 18px;
  height: 18px;
`;

const TermsBox = styled.div`
  margin: 24px 0 0 0;
`;

const TermsTitle = styled.div`
  color: #3d1690;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const TermsText = styled.div`
  color: #737373;
  font-size: 12px;
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IntroSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 136px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e2e2e2;
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  justify-content: center;
`;

const IntroText = styled.p`
  color: #2e2e2e;
  font-family: 'Plus Jakarta Sans';
  font-size: 14px;
  font-weight: 400;
  line-height: 19.6px;
  margin: 0;
`;

const IntroSubText = styled.p`
  color: #737373;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-weight: 400;
  line-height: 16.8px;
  margin: 0;
`;

const ImageContainer = styled.div`
  width: 136px;
  height: 136px;
  background: #f7f7f7;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

interface ReviewWritePcFormProps {
  rating: number;
  setRating: (v: number) => void;
  title: string;
  setTitle: (v: string) => void;
  content: string;
  setContent: (v: string) => void;
  nickname: string;
  setNickname: (v: string) => void;
  skinType: string;
  setSkinType: (v: string) => void;
  skinConcern: string;
  setSkinConcern: (v: string) => void;
  images: File[];
  setImages: (v: File[]) => void;
  recommend: string;
  setRecommend: (v: string) => void;
  isSubmitDisabled: boolean;
  onSubmit: () => void;
  productImage: string;
  skinTypeOptions: SelectOption[];
  skinConcernOptions: SelectOption[];
}

export const ReviewWritePcForm = ({
  rating,
  setRating,
  title,
  setTitle,
  content,
  setContent,
  nickname,
  setNickname,
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  images,
  setImages,
  recommend,
  setRecommend,
  isSubmitDisabled,
  onSubmit,
  productImage,
  skinTypeOptions,
  skinConcernOptions,
}: ReviewWritePcFormProps) => {
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArr = Array.from(files);
      setImages([...images, ...fileArr]);
    }
  };
  const handleRemoveImage = (idx: number) => {
    setImages(images.filter((_, i) => i !== idx));
  };

  // File[] -> string[] (preview url) 변환
  const imagePreviews = useMemo(
    () =>
      images.map((file) => {
        return URL.createObjectURL(file);
      }),
    [images],
  );

  return (
    <>
      <IntroSection>
        <TextContainer>
          <IntroText>Tell others what you think about this skincare product.</IntroText>
          <IntroSubText>Your honest feedback helps fellow customers make informed decisions.</IntroSubText>
        </TextContainer>
        <ImageContainer>
          <ProductImage src={productImage} alt="Product" />
        </ImageContainer>
      </IntroSection>
      <Divider />
      <Section>
        <Label>
          Overall rating <span style={{ color: '#2E2E2E' }}>*</span>
        </Label>
        <StarRow>
          {[1, 2, 3, 4, 5].map((num) => (
            <Star
              key={num}
              type="button"
              selected={rating >= num}
              onClick={() => setRating(num)}
              aria-label={`별점 ${num}점`}
            >
              <StarIcon filled={rating >= num} />
            </Star>
          ))}
        </StarRow>
      </Section>
      <Section>
        <Label>
          More about you <span style={{ color: '#2E2E2E' }}>*</span>
        </Label>
        <Row>
          <Section style={{ flex: 1 }}>
            <Select options={skinTypeOptions} value={skinType} onChange={setSkinType} title="Select" width="100%" />
          </Section>
          <Section style={{ flex: 1 }}>
            <Select
              options={skinConcernOptions}
              value={skinConcern}
              onChange={setSkinConcern}
              title="Select"
              width="100%"
            />
          </Section>
        </Row>
      </Section>

      <Section>
        <Label htmlFor="review-content">
          Your review <span style={{ color: '#2E2E2E' }}>*</span>
        </Label>
        <TextArea id="review-content" maxLength={5000} value={content} onChange={(e) => setContent(e.target.value)} />
        <GuideText>• 5000 characters max</GuideText>
      </Section>
      <Section>
        <Label htmlFor="review-title">
          Add a title <span style={{ color: '#2E2E2E' }}>*</span>
        </Label>
        <Input id="review-title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
        <GuideText>• 200 characters max</GuideText>
      </Section>
      <Section>
        <LabelWrapper>
          <Label>Add photos</Label>
          <Optional>Optional</Optional>
        </LabelWrapper>
        <ImageUploadBox>
          {images.map((file, idx) => (
            <Thumbnail key={idx}>
              <img
                src={imagePreviews[idx]}
                alt={`업로드 이미지 ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
              />
              <RemoveButton type="button" onClick={() => handleRemoveImage(idx)} aria-label="이미지 삭제">
                ×
              </RemoveButton>
            </Thumbnail>
          ))}
          {images.length < 5 &&
            (images.length === 0 ? (
              <UploadArea htmlFor="review-image">
                <PlusIcon />
                <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
              </UploadArea>
            ) : (
              <SmallUploadArea htmlFor="review-image">
                <PlusIcon />
                <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
              </SmallUploadArea>
            ))}
        </ImageUploadBox>
      </Section>
      <Section>
        <LabelWrapper>
          <Label>Would you recommend this item</Label>
        </LabelWrapper>
        <RadioLabelWrapper>
          <RadioGroup>
            <RadioLabel>
              <Radio
                type="radio"
                name="recommend"
                value="yes"
                checked={recommend === 'yes'}
                onChange={() => setRecommend('yes')}
              />
              Yes
            </RadioLabel>
            <RadioLabel>
              <Radio
                type="radio"
                name="recommend"
                value="no"
                checked={recommend === 'no'}
                onChange={() => setRecommend('no')}
              />
              No
            </RadioLabel>
          </RadioGroup>
        </RadioLabelWrapper>
      </Section>
      <Section>
        <Label htmlFor="review-nickname">Create a display name</Label>
        <Input id="review-nickname" maxLength={25} value={nickname} onChange={(e) => setNickname(e.target.value)} />
        <GuideText>
          • Must be 4 to 25 characters.
          <br />• Please don't use spaces, special characters or emojis
        </GuideText>
      </Section>
      <TermsBox>
        <TermsTitle>Terms & Conditions</TermsTitle>
        <TermsText>The SkinSeoul team will publish it after reviewing the content.</TermsText>
      </TermsBox>
      <BottomButtonRow>
        <SubmitButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
          Submit review
        </SubmitButton>
      </BottomButtonRow>
    </>
  );
};
